import React, { useEffect, useState } from 'react';
import { authService } from '../../services/authService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Verificar autenticación con un pequeño delay para asegurar que sessionStorage esté disponible
    const checkAuth = async () => {
      // Debug: mostrar contenido de sessionStorage
      if (process.env.NODE_ENV === 'development') {
        authService.debugSessionStorage();
      }

      try {
        const authenticated = await authService.isAuthenticated();
        setIsAuthenticated(authenticated);
        setIsChecking(false);

        // Si no hay token después de verificar, redirigir a starmap
        if (!authenticated) {
          // Pequeño delay para evitar redirecciones innecesarias
          setTimeout(async () => {
            const stillNotAuthenticated = !(await authService.isAuthenticated());
            if (stillNotAuthenticated) {
              console.log('⚠️ No se encontró token, redirigiendo a starmap...');
              authService.redirectToStarmap();
            }
          }, 100);
        } else {
          // Validar el token contra la API si existe
          const token = sessionStorage.getItem('corvus_token');
          if (token) {
            const validation = await authService.validateToken(token);
            if (!validation.valid) {
              console.error('❌ Token inválido según la API:', validation.error);
              // Limpiar token inválido
              sessionStorage.removeItem('corvus_token');
              sessionStorage.removeItem('user_data');
              setIsAuthenticated(false);
              authService.redirectToStarmap();
              return;
            }
            console.log('✅ Token válido según la API, usuario autenticado');
          }
        }
      } catch (error) {
        console.error('Error verificando autenticación:', error);
        setIsChecking(false);
        setIsAuthenticated(false);
      }
    };

    // Verificar inmediatamente
    checkAuth();

    // También escuchar cambios en sessionStorage (por si el token se guarda después)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'corvus_token' && e.newValue) {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Verificar periódicamente por si el token se guarda desde otro contexto
    const interval = setInterval(async () => {
      if (!isAuthenticated) {
        const authenticated = await authService.isAuthenticated();
        if (authenticated) {
          checkAuth();
        }
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  if (isChecking) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <h2>Verificando autenticación...</h2>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <h2>Redirigiendo a autenticación...</h2>
        <p>Serás redirigido a starmap.corvusnocta.org para iniciar sesión.</p>
      </div>
    );
  }

  return <>{children}</>;
}; 